import { useState } from "react";
import { useDemo } from "../zustand/DemoZustand";
import Verknuepfungen from "./Verknuepfungen";
import { Fragezeichen, Haken, Kalender, Kreuz, Ordner, Uhr } from "./Symbole";
import { meineTeilnahme, nameVon, teilnahmeZaehlung } from "../hilfen/auswertung";
import { langesDatum, uhrzeit } from "../hilfen/datum";

/*
  Ein Termin, aufgeklappt — im selben Zuschnitt wie die Aufgabenkarte:
  Kopfleiste, links der Inhalt, rechts die Angaben.

  Ein Termin gehört den EINGELADENEN. Wer ihn einträgt, ist automatisch
  eingeladen; wer nicht eingeladen ist, sieht ihn nicht — und zwar schon auf
  dem Server, nicht erst hier.

  Zu- und Absagen laufen über dieselbe Sammlung wie bei den Sitzungen. Das
  Feld dort ist ein Textfeld, also passt die Kennung eines Termins hinein;
  es braucht keine zweite Zählung und keine zweite Bedienung.
*/

export default function Terminkarte({ terminId, aufSchliessen, oeffne }) {
  const { zustand, ich, aktionen } = useDemo();
  const termin = (zustand.termine || []).find((t) => t.id === terminId);

  if (!termin) {
    return (
      <div className="ueberlagerung" role="dialog" aria-label="Termin nicht gefunden">
        <div className="ueberlagerung__karte">
          <p style={{ fontWeight: 600 }}>Diesen Termin gibt es nicht mehr.</p>
          <p className="klein leise" style={{ marginTop: "var(--luft-2)" }}>
            Er wurde gelöscht, oder du bist nicht mehr eingeladen.
          </p>
          <button
            className="knopf knopf--zurueckhaltend knopf--breit"
            style={{ marginTop: "var(--luft-4)" }}
            onClick={aufSchliessen}
          >
            Schließen
          </button>
        </div>
      </div>
    );
  }

  const meine = meineTeilnahme(zustand, ich.id, termin.id);
  const zaehlung = teilnahmeZaehlung(zustand, termin.id);
  const eigener = termin.verfasserId === ich.id;

  return (
    <div className="ueberlagerung" role="dialog" aria-label={termin.titel}>
      <div className="ueberlagerung__karte ueberlagerung__karte--breit aufgabenkarte">
        <div className="aufgabenkarte__leiste">
          <div className="aufgabenkarte__leiste-links">
            <span className="klein leise">
              <Kalender groesse={14} /> Termin der Fraktion
            </span>
          </div>

          <button className="knopf knopf--schlicht" aria-label="Schließen" onClick={aufSchliessen}>
            <Kreuz groesse={18} />
          </button>
        </div>

        <h2 className="aufgabenkarte__titel">{termin.titel}</h2>

        <p className="klein leise aufgabenkarte__beschreibung">
          {langesDatum(termin.beginn)}, {uhrzeit(termin.beginn)}
          {termin.ende ? ` bis ${uhrzeit(termin.ende)}` : ""}
          {termin.ort ? ` · ${termin.ort}` : ""}
        </p>

        {termin.beschreibung && (
          <p className="klein aufgabenkarte__beschreibung">{termin.beschreibung}</p>
        )}

        <div className="aufgabenkarte__raster">
          <div className="aufgabenkarte__haupt">
            <p className="aufgabenkarte__frage">Kommst du?</p>

            <div className="wahl wahl--eng">
              {[
                ["ja", "Ja", Haken],
                ["nein", "Nein", Kreuz],
                ["unklar", "Unklar", Fragezeichen],
              ].map(([wert, beschriftung, Symbol]) => (
                <button
                  key={wert}
                  className="wahl__knopf"
                  aria-pressed={meine === wert}
                  onClick={() => aktionen.setzeTeilnahme(termin.id, wert)}
                >
                  <Symbol groesse={16} />
                  {beschriftung}
                </button>
              ))}
            </div>

            <p className="klein leise" style={{ marginTop: "var(--luft-2)" }}>
              {zaehlung.ja} zugesagt, {zaehlung.nein} abgesagt, {zaehlung.unklar} unklar.
            </p>

            <p className="aufgabenkarte__frage">Was gehört dazu?</p>
            <Verknuepfungen
              verknuepfungen={termin.verknuepfungen}
              aufAendern={(neu) => aktionen.aendereTermin(termin.id, { verknuepfungen: neu })}
              oeffne={oeffne}
              aufSchliessen={aufSchliessen}
            />
          </div>

          <aside className="aufgabenkarte__seite">
            <Angabe symbol={Uhr} name="Wann">
              <input
                type="datetime-local"
                className="brett__feld"
                value={alsFeld(termin.beginn)}
                onChange={(e) =>
                  e.target.value && aktionen.aendereTermin(termin.id, { beginn: e.target.value })
                }
                aria-label="Beginn"
              />
            </Angabe>

            <Angabe symbol={Ordner} name="Wo">
              <input
                className="brett__feld"
                defaultValue={termin.ort}
                placeholder="Ort"
                onBlur={(e) => aktionen.aendereTermin(termin.id, { ort: e.target.value })}
                aria-label="Ort"
              />
            </Angabe>

            <Angabe name="Eingeladen">
              <Einladung termin={termin} />
            </Angabe>

            {eigener && (
              <button
                className="knopf knopf--schlicht knopf--breit aufgabenkarte__loeschen"
                onClick={async () => {
                  await aktionen.loescheTermin(termin.id);
                  aufSchliessen();
                }}
              >
                Termin löschen
              </button>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}

/* Eine Zeile in der rechten Spalte — wie an der Aufgabenkarte. */
function Angabe({ symbol: Symbol, name, children }) {
  return (
    <div className="angabe">
      <p className="angabe__name">
        {Symbol && <Symbol groesse={14} />}
        {name}
      </p>
      <div className="angabe__wert">{children}</div>
    </div>
  );
}

/*
  Wer eingeladen ist, sieht den Termin — deshalb steht neben der Auswahl ein
  Satz dazu. Das Ausladen einer Person nimmt ihr den Termin aus dem Kalender;
  das ist kein Schönheitsfehler, sondern der Sinn der Sache, und man muss es
  wissen, bevor man es tut.
*/
function Einladung({ termin }) {
  const { stammdaten, ich, aktionen } = useDemo();
  const [offen, setzeOffen] = useState(false);

  const drin = termin.eingeladenIds || [];
  const waehlbar = Object.values(stammdaten.personen);

  function umschalten(personId) {
    /* Wer eingetragen hat, bleibt drin — sonst verlöre er den eigenen Termin. */
    if (personId === termin.verfasserId) return;
    const neu = drin.includes(personId)
      ? drin.filter((id) => id !== personId)
      : [...drin, personId];
    return aktionen.aendereTermin(termin.id, { eingeladenIds: neu });
  }

  return (
    <>
      <div className="etiketten">
        {drin.map((id) => (
          <button
            key={id}
            className="etikett etikett--weg"
            onClick={() => umschalten(id)}
            title={id === termin.verfasserId ? "Hat den Termin eingetragen" : "Ausladen"}
          >
            {nameVon(stammdaten, id, ich.id)}
            {id !== termin.verfasserId && <Kreuz groesse={12} />}
          </button>
        ))}

        <button className="etikett etikett--mehr" onClick={() => setzeOffen(!offen)}>
          {offen ? "Fertig" : "+ Einladen"}
        </button>
      </div>

      {offen && (
        <div className="wahl wahl--eng" style={{ marginTop: "var(--luft-2)" }}>
          {waehlbar.map((person) => (
            <button
              key={person.id}
              className="wahl__knopf"
              aria-pressed={drin.includes(person.id)}
              disabled={person.id === termin.verfasserId}
              onClick={() => umschalten(person.id)}
            >
              {nameVon(stammdaten, person.id, ich.id)}
            </button>
          ))}
        </div>
      )}

      <p className="klein leise" style={{ marginTop: "var(--luft-2)" }}>
        Nur Eingeladene sehen den Termin — auch in ihrem Handykalender.
      </p>
    </>
  );
}

/* Date-Objekt → „2026-08-08T10:00“, wie das Feld es erwartet (Ortszeit). */
function alsFeld(datum) {
  if (!datum) return "";
  const d = new Date(datum);
  if (isNaN(d.getTime())) return "";
  const zweistellig = (n) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${zweistellig(d.getMonth() + 1)}-${zweistellig(d.getDate())}` +
    `T${zweistellig(d.getHours())}:${zweistellig(d.getMinutes())}`
  );
}
